/**
 * locale -- the ONE place that turns the active i18n language into the
 * locale tags the formatters need (Intl, date-fns, moment).
 *
 * Before 2026-09-23 every screen hard-coded 'fr-FR' or `{ locale: fr }`, so an
 * English UI still printed French dates. Use these helpers instead:
 *
 *   currentLocale()            -> 'fr-FR' | 'en-US'   (Intl.*, toLocaleDateString)
 *   currentDateFnsLocale()     -> fr | enUS           (date-fns `format(..., { locale })`)
 *   formatDate(d, options)     -> Intl.DateTimeFormat in the active locale
 *   formatNumber(n, options)   -> Intl.NumberFormat in the active locale
 *   formatCurrency(n)          -> EUR amount in the active locale
 *   formatPercent(points)      -> "12,5 %" | "12.5%" from 12.5
 *
 * Components re-render on language change as long as they call
 * useTranslation(); the helpers read the language at call time.
 */

import i18n from 'i18next';
import { fr, enUS } from 'date-fns/locale';
import type { Locale } from 'date-fns';

export type AppLanguage = 'fr' | 'en';

const LOCALE_TAG: Record<AppLanguage, string> = { fr: 'fr-FR', en: 'en-US' };
const DATE_FNS_LOCALE: Record<AppLanguage, Locale> = { fr, en: enUS };

/** 'fr' or 'en', whatever i18next currently resolves to. */
export function currentLanguage(): AppLanguage {
  const lng = (i18n.language || i18n.options?.fallbackLng || 'fr').toString().toLowerCase();
  return lng.startsWith('en') ? 'en' : 'fr';
}

/** BCP 47 tag for Intl and toLocale*String. */
export function currentLocale(): string {
  return LOCALE_TAG[currentLanguage()];
}

/** date-fns locale object for `format(date, pattern, { locale })`. */
export function currentDateFnsLocale(): Locale {
  return DATE_FNS_LOCALE[currentLanguage()];
}

export function formatDate(
  value: string | number | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' },
): string {
  if (value === null || value === undefined || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(currentLocale(), options).format(d);
}

export function formatTime(
  value: string | number | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' },
): string {
  return formatDate(value, options);
}

export function formatDateTime(
  value: string | number | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' },
): string {
  return formatDate(value, options);
}

export function formatNumber(value: number | null | undefined, options: Intl.NumberFormatOptions = {}): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return new Intl.NumberFormat(currentLocale(), options).format(value);
}

/**
 * A rate given in percent points (12.5 means 12.5 %), in the active locale:
 * "12,5 %" in French (narrow no-break space before the sign), "12.5%" in
 * English. Every analytics figure goes through here so the same rate never
 * reads "12.5%" on one tile and "12,5 %" on the next.
 */
export function formatPercent(points: number | null | undefined, fractionDigits = 1): string {
  const v = Number(points);
  // ICU versions disagree on the space before "%" in French (U+00A0 in
  // ICU 78, U+202F in some browsers). French typography wants the narrow one.
  return formatNumber(Number.isFinite(v) ? v / 100 : 0, {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).replace('\u00a0%', '\u202f%');
}

export function formatCurrency(value: number | null | undefined, currency = 'EUR', maximumFractionDigits = 0): string {
  return formatNumber(value ?? 0, { style: 'currency', currency, maximumFractionDigits });
}

// ── Durées ──────────────────────────────────────────────────────────────────
//
// 🚨 Quatre `formatDuration` vivaient dans quatre écrans le 2026-09-30, avec
// quatre résultats pour la même valeur : « 3min 20s », « 3m 20s », « 03:20 »
// et « 0 min » pour rien. Deux besoins réels s'y cachaient, et un seul de
// chaque vit ici désormais.

/** « 03:20 », « 1:05:03 » : la durée telle qu'un lecteur audio l'affiche. */
export function formatDurationClock(seconds: number | null | undefined): string {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const deuxChiffres = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${deuxChiffres(m)}:${deuxChiffres(s)}` : `${deuxChiffres(m)}:${deuxChiffres(s)}`;
}

function unite(valeur: number, unit: 'hour' | 'minute' | 'second'): string {
  return new Intl.NumberFormat(currentLocale(), { style: 'unit', unit, unitDisplay: 'short' }).format(valeur);
}

/**
 * « 3 min 20 s », « 1 h 5 min », « 45 s » : la durée telle qu'on la dit.
 *
 * Les unités viennent d'Intl, donc elles suivent la langue active sans
 * qu'aucun catalogue ait à les porter.
 */
export function formatDurationWords(seconds: number | null | undefined): string {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  if (total < 60) return unite(total, 'second');
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return m > 0 ? `${unite(h, 'hour')} ${unite(m, 'minute')}` : unite(h, 'hour');
  return s > 0 ? `${unite(m, 'minute')} ${unite(s, 'second')}` : unite(m, 'minute');
}

// ── Dates nommées ───────────────────────────────────────────────────────────
//
// Chaque écran appelait son propre `formatDate` : la même date s'affichait
// « 30/09/2026 14:12 », « 30 sept. 2026 », « mar. 30 sept. » et « 30/09/26 »
// selon la page. Les quatre formes restent, mais elles portent un nom et
// une seule implémentation.

/** « 30 sept. 2026 » : une date qu'on lit, sans heure. */
export function formatDayMonthYear(value: string | number | Date | null | undefined): string {
  return formatDate(value, { day: '2-digit', month: 'short', year: 'numeric' });
}

/** « mar. 30 sept. » : un repère dans la semaine, l'année étant évidente. */
export function formatWeekdayDay(value: string | number | Date | null | undefined): string {
  return formatDate(value, { weekday: 'short', day: 'numeric', month: 'short' });
}

/** « 30/09/26 » : la forme la plus courte, pour une colonne étroite. */
export function formatShortDate(value: string | number | Date | null | undefined): string {
  return formatDate(value, { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/**
 * « il y a 3 min », « hier », « 30 sept. » : depuis quand.
 *
 * 🚨 Deux versions coexistaient le 2026-09-30. Celle du fil d'activité CRM
 * écrivait « à l'instant », « il y a 3min » et « hier » EN DUR EN FRANÇAIS,
 * donc une interface en anglais les affichait en français. Intl porte ces
 * formulations dans toutes les langues ; aucun catalogue n'a à les répéter.
 *
 * Au-delà d'une semaine, « il y a 34 jours » ne dit plus rien d'utile : la
 * date elle-même est plus lisible.
 */
export function formatRelativeTime(
  value: string | number | Date | null | undefined,
  now: Date = new Date(),
): string {
  if (value === null || value === undefined || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const secondes = Math.round((d.getTime() - now.getTime()) / 1000);
  const passe = Math.abs(secondes);
  const rtf = new Intl.RelativeTimeFormat(currentLocale(), { numeric: 'auto' });

  if (passe < 60) return rtf.format(Math.trunc(secondes), 'second');
  if (passe < 3600) return rtf.format(Math.trunc(secondes / 60), 'minute');
  if (passe < 86_400) return rtf.format(Math.trunc(secondes / 3600), 'hour');
  if (passe < 7 * 86_400) return rtf.format(Math.trunc(secondes / 86_400), 'day');
  return formatDate(d, { day: '2-digit', month: 'short' });
}
